import React, { useState, useEffect, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axiosUtil';
import { DateUtil } from 'doctivity-shared/utils';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Button,
  Divider,
  Snackbar,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import {
  copyToClipboard,
  notificationLink,
  notificationTarget,
  hasOwnPage,
} from './NotificationView';

// The bell shows the most recent slice; the full history lives on /notifications.
const BELL_PAGE_SIZE = 10;

// Width of one small IconButton (20px icon + 5px padding each side). Holds the
// copy-link slot open on rows that don't have one so the actions stay aligned.
const ACTION_SLOT = 30;

/**
 * Notification bell for the current user, beside the dashboard greeting. Reads
 * /api/v2/my/notifications (that user's active recipient rows — a flat per-user
 * lookup) and shows the most recent few with an unread badge.
 *
 * The bell is a pointer, not a reader: picking a notification navigates to its
 * own page at /notifications/:id. That keeps one place responsible for rendering
 * a notification (and for marking it read), and it means what you see from the
 * bell is the same URL you'd get from a shared link or an email — no second,
 * slightly-different modal version of the same thing.
 */
const NotificationBell = ({ sx }) => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [toast, setToast] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/v2/my/notifications', {
        params: { limit: BELL_PAGE_SIZE, offset: 0 },
      });
      setItems(Array.isArray(data?.notifications) ? data.notifications : []);
      setUnread(data?.unread_count || 0);
      setTotal(data?.count || 0);
    } catch (e) {
      setItems([]);
      setUnread(0);
      setTotal(0);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openInbox = (e) => setAnchorEl(e.currentTarget);
  const closeInbox = () => setAnchorEl(null);

  // An announcement opens its own page (which marks it read once loaded); a
  // system_event goes straight to the note or mileage log it's telling you about.
  // See notificationTarget.
  const openItem = (item) => {
    closeInbox();
    const target = notificationTarget(item);
    // Going straight to the note skips NotificationPage, which is what normally
    // marks a notification read — so mark it here instead. Otherwise every note
    // assignment would stay unread forever and the badge would only ever climb.
    // Deliberately fire-and-forget: this is a user's explicit click, not a GET
    // side effect, and it must not delay the navigation.
    if (target !== item.permalink && !item.read_at) {
      axiosInstance.post(`/v2/my/notifications/${item.id}/read`).catch(() => {});
    }
    navigate(target);
  };

  // Delete a notification from the user's own list (soft-delete of their recipient
  // row, server-side). Works for any message type, announcements included.
  const deleteMessage = async (item) => {
    setItems((list) => list.filter((i) => i.id !== item.id));
    setTotal((n) => Math.max(0, n - 1));
    if (!item.read_at) setUnread((n) => Math.max(0, n - 1));
    try {
      await axiosInstance.delete(`/v2/my/notifications/${item.id}`);
    } catch (e) {
      /* non-fatal */
    }
  };

  const copyLink = async (item) => {
    const ok = await copyToClipboard(notificationLink(item));
    setToast(ok ? 'Link copied' : 'Could not copy the link');
  };

  return (
    <Box sx={sx}>
      <IconButton
        onClick={openInbox}
        size='large'
        color='inherit'
        aria-label='Notifications'
        // Idle (nothing to read) → muted so it doesn't pop; unread or hover → full color.
        sx={{
          opacity: unread > 0 ? 1 : 0.4,
          transition: 'opacity 0.15s ease',
          '&:hover': { opacity: 1 },
        }}
      >
        <Badge badgeContent={unread} color='error'>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeInbox}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 360, maxWidth: '90vw' }}>
          <Typography variant='subtitle1' sx={{ px: 2, py: 1.5, fontWeight: 600 }}>
            Notifications
          </Typography>
          <Divider />
          {items.length === 0 ? (
            <Typography sx={{ p: 2 }} color='textSecondary'>
              You&apos;re all caught up.
            </Typography>
          ) : (
            <List disablePadding sx={{ maxHeight: 420, overflowY: 'auto' }}>
              {items.map((item) => (
                <ListItemButton
                  key={item.id}
                  onClick={() => openItem(item)}
                  sx={{ '& .row-btn': { visibility: 'hidden' }, '&:hover .row-btn': { visibility: 'visible' } }}
                >
                  <ListItemText
                    primary={item.title}
                    secondary={item.created_at ? DateUtil.formatFromDB(item.created_at) : ''}
                    primaryTypographyProps={{ fontWeight: item.read_at ? 400 : 700 }}
                  />
                  {!item.read_at && (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', ml: 1 }} />
                  )}
                  {/* Only announcements have a page worth linking to (see hasOwnPage),
                      but the slot is always reserved so the delete icon lines up on
                      every row rather than sliding left on note rows. */}
                  <Box
                    component='span'
                    sx={{ display: 'inline-flex', width: ACTION_SLOT, ml: 0.5 }}
                  >
                    {hasOwnPage(item) && (
                      <IconButton
                        className='row-btn'
                        size='small'
                        aria-label='Copy link'
                        title='Copy link to this notification'
                        onClick={(e) => {
                          e.stopPropagation();
                          copyLink(item);
                        }}
                      >
                        <LinkIcon fontSize='small' />
                      </IconButton>
                    )}
                  </Box>
                  <IconButton
                    className='row-btn'
                    size='small'
                    aria-label='Delete'
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMessage(item);
                    }}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </ListItemButton>
              ))}
            </List>
          )}
          <Divider />
          <Box sx={{ px: 1, py: 0.5, textAlign: 'center' }}>
            <Button
              size='small'
              color='primary'
              onClick={() => {
                closeInbox();
                navigate('/notifications');
              }}
            >
              {total > items.length ? `View all notifications (${total})` : 'View all notifications'}
            </Button>
          </Box>
        </Box>
      </Popover>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

NotificationBell.propTypes = {
  sx: PropTypes.object,
};

export { NotificationBell };
export default NotificationBell;
